export enum GrpTargetTags {
  GUESTS = "guests",
  NO_GUESTS = "no_guests",
  BUYERS = "buyers",
  NO_BUYERS = "no_buyers",
  MALE = "male",
  FEMALE = "female",
  AGE_4_7 = "4_7",
  AGE_8_14 = "8_14",
  AGE_15_19 = "15_19",
  AGE_20_24 = "20_24",
  AGE_25_34 = "25_34",
  AGE_35_44 = "35_44",
  AGE_45_54 = "45_54",
  AGE_55_64 = "55_64",
  AGE_OVER_64 = "over_64",
  CUSTOM = "custom",
  KIDS_0_3 = "kids_0_3",
  KIDS_4_7 = "kids_4_7",
  KIDS_8_14 = "kids_8_14",
  KIDS_0_7 = "kids_0_7",
  KIDS_0_14 = "kids_0_14",
  KIDS_4_14 = "kids_4_14",
}

const ageRangeSorter: (a: AgeRange, b: AgeRange) => number = (a, b) => {
  return a.start - b.start;
};

type MaybeAgeRange = AgeRange | NotAgeRange;

class NotAgeRange {
  success = false;
}

class AgeRange {
  success = true;
  start!: number;
  end!: number;

  constructor(start: number, end: number = Number.POSITIVE_INFINITY) {
    this.start = start;
    this.end = end;
  }

  merge(range: AgeRange): MaybeAgeRange {
    const minStart = Math.min(this.start, range.start);
    const maxEnd = Math.max(this.end, range.end);

    if (this.contiguous(range)) {
      return new AgeRange(minStart, maxEnd);
    } else {
      return new NotAgeRange();
    }
  }

  contiguous(range: AgeRange): boolean {
    return (
      this.end == range.start - 1 ||
      this.start == range.end + 1 ||
      this.end == range.start ||
      this.start == range.end
    );
  }

  toString(): string {
    if (this.end == Number.POSITIVE_INFINITY) {
      if (this.start < 60) return `${this.start}+`;
      else return `${this.start + 1}+`;
    }
    return `${this.start}-${this.end}`;
  }
}

export default class GrpTargetsTagsParser {
  private tagsList = new Set<GrpTargetTags>();
  originalTags: string[][] = [[]];

  constructor(tags: string[][]) {
    this.originalTags = tags;
    this.originalTags
      .flat()
      .forEach((t) => this.tagsList.add(t as GrpTargetTags));
  }

  get tags() {
    return this.tagsList;
  }

  get guests(): boolean {
    return this.tagsList.has(GrpTargetTags.GUESTS);
  }

  get buyers(): boolean {
    return this.tagsList.has(GrpTargetTags.BUYERS);
  }

  get male(): boolean {
    return this.tagsList.has(GrpTargetTags.MALE);
  }

  get female(): boolean {
    return this.tagsList.has(GrpTargetTags.FEMALE);
  }

  get sexTags(): GrpTargetTags[] {
    if (this.male) {
      if (this.female) {
        return [GrpTargetTags.MALE, GrpTargetTags.FEMALE];
      } else {
        return [GrpTargetTags.MALE];
      }
    } else {
      return [GrpTargetTags.FEMALE];
    }
  }

  get kidsAges(): AgeRange[] {
    const kidsAgeRanges: AgeRange[] = [];
    this.tags.forEach((t) => {
      const matches = [...t.toString().matchAll(/^kids_(\d+)_(\d+)$/g)];
      if (matches[0]) {
        const match = matches[0];
        kidsAgeRanges.push(
          new AgeRange(
            parseInt(match[1] as unknown as string),
            parseInt(match[2] as unknown as string)
          )
        );
      }
    });
    return kidsAgeRanges.sort(ageRangeSorter);
  }

  get ages(): AgeRange[] {
    const ranges: AgeRange[] = [];
    this.tags.forEach((t) => {
      const matches = [...t.toString().matchAll(/^(\d+|over)_(\d+)$/g)];
      if (matches[0]) {
        const match = matches[0];
        if ((match[1] as unknown as string) == "over") {
          ranges.push(new AgeRange(parseInt(match[2] as unknown as string)));
        } else {
          ranges.push(
            new AgeRange(
              parseInt(match[1] as unknown as string),
              parseInt(match[2] as unknown as string)
            )
          );
        }
      }
    });
    ranges.sort(ageRangeSorter);
    return ranges;
  }

  get ageRangeString(): string {
    return GrpTargetsTagsParser.ageRangeToString(this.ages);
  }

  get kidsAgesString(): string {
    return GrpTargetsTagsParser.ageRangeToString(this.kidsAges);
  }

  get sexString(): string {
    const tags = this.sexTags;
    if (tags.length == 2) {
      return "Femmine, Maschi";
    } else {
      return tags[0] == GrpTargetTags.FEMALE ? "Femmine" : "Maschi";
    }
  }

  private static ageRangeToString(ages: AgeRange[]): string {
    let lastPass = Number.POSITIVE_INFINITY;
    let lastAges = this.mergeAgeRanges(ages);
    let exit = false;
    while (!exit) {
      lastAges = this.mergeAgeRanges(lastAges);
      if (lastAges.length < lastPass) {
        lastPass = lastAges.length;
      } else {
        exit = true;
      }
    }
    return lastAges.toString().replace(new RegExp(",", "g"), ", ");
  }

  private static mergeAgeRanges(ages: AgeRange[]): AgeRange[] {
    let i = 0;
    while (i + 1 < ages.length) {
      const range = ages[i].merge(ages[i + 1]);
      if (range.success) {
        ages[i] = range as AgeRange;
        ages.splice(i + 1, 1);
      }
      i++;
    }
    return ages;
  }
}
